// campaignShareUrl.js
//
// Canonical, PURE helpers for building and parsing campaign-tagged file share
// URLs. No I/O, no network, no Date/random -> fully deterministic and unit
// testable.
//
// A campaign drives traffic to a public file (brochure) via its public share
// URL. We append UTM params + a stable ContextStory campaign id so that a later
// file access can be attributed back to the campaign that caused it.
//
//   <baseFileUrl>?...&utm_source=..&utm_medium=..&utm_campaign=<slug>&cs_campaign=<id>
//
// KEEP IN SYNC with the Node test/harness copy:
//   scripts/propertyassets-enrichment/campaign-tagged-share-url/campaignShareUrl.mjs
// The two files are intentionally byte-identical in their function bodies so the
// browser UI and the Node test harness produce the same URLs. If you change one,
// change the other. The .mjs copy has the unit tests (test_campaign_share_url.mjs).
//
// ATTRIBUTION SEAM (read this): the not-yet-built file access-analytics feature
// is expected to read the query params `cs_campaign` (campaign _id) and the
// `utm_*` values off the incoming request URL and record them against the file
// hit. `parseCampaignFileUrl()` below is the reference decoder for exactly those
// params -- it is the contract that the analytics capture endpoint must honor.

/** Default UTM values when a campaign does not override them in data.tracking. */
export const DEFAULT_TRACKING = {
  utmSource: "contextstory",
  utmMedium: "campaign",
};

/** Default backend origin for the tracked redirect endpoint. */
export const DEFAULT_BACKEND_ORIGIN = "https://backend.prod.oelement.net";

/** The query-param names we read/write. Centralized so decoder and encoder agree. */
export const PARAM_KEYS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
  campaignId: "cs_campaign",
};

/**
 * Convert an arbitrary campaign name into a URL-safe, human-readable slug.
 * "Q3 Bridge Loan Push!" -> "q3-bridge-loan-push"
 */
export const slugify = (value) =>
  String(value == null ? "" : value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Resolve the tracking payload for a campaign. This is what should be persisted
 * under `campaign.data.tracking` AND what feeds the URL builder, so the stored
 * metadata and the generated URL never drift.
 *
 * Precedence: explicit campaign.data.tracking.* overrides -> DEFAULT_TRACKING ->
 * slug derived from the campaign name (fallback to the campaign id).
 *
 * @returns {{utmSource,utmMedium,utmCampaign,utmContent?,utmTerm?,campaignId}}
 */
export const buildCampaignTracking = (campaign) => {
  const c = campaign || {};
  const t = (c.data && c.data.tracking) || {};
  const campaignId = c.id != null ? String(c.id) : "";
  const utmCampaign = t.utmCampaign || slugify(c.name) || campaignId;
  const tracking = {
    utmSource: t.utmSource || DEFAULT_TRACKING.utmSource,
    utmMedium: t.utmMedium || DEFAULT_TRACKING.utmMedium,
    utmCampaign,
    campaignId,
  };
  if (t.utmContent) tracking.utmContent = String(t.utmContent);
  if (t.utmTerm) tracking.utmTerm = String(t.utmTerm);
  return tracking;
};

/**
 * Append campaign attribution params to a file's public share URL.
 * Pure: given the same inputs it always returns the same string.
 *
 * - Preserves any existing query params on baseUrl (only overrides the
 *   utm_* / cs_campaign keys it manages).
 * - Preserves a trailing #fragment.
 * - Throws if there is no baseUrl or no stable campaign id, because an
 *   un-attributable URL is a silent data-loss bug for the analytics feature.
 *
 * @param {string} baseUrl  the file's public URL (may already have ?query and/or #hash)
 * @param {object} campaign { id, name, data?: { tracking?: {...} } }
 * @returns {string} the campaign-tagged URL
 */
export const buildCampaignFileUrl = (baseUrl, campaign) => {
  if (!baseUrl || typeof baseUrl !== "string") {
    throw new Error("buildCampaignFileUrl: baseUrl is required");
  }
  const tracking = buildCampaignTracking(campaign);
  if (!tracking.campaignId) {
    throw new Error("buildCampaignFileUrl: campaign.id is required for attribution");
  }

  // Split off the fragment so we never corrupt it, then split query from path.
  const hashIndex = baseUrl.indexOf("#");
  const hash = hashIndex >= 0 ? baseUrl.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? baseUrl.slice(0, hashIndex) : baseUrl;
  const qIndex = withoutHash.indexOf("?");
  const path = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
  const queryStr = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : "";

  const params = new URLSearchParams(queryStr);
  params.set(PARAM_KEYS.utmSource, tracking.utmSource);
  params.set(PARAM_KEYS.utmMedium, tracking.utmMedium);
  params.set(PARAM_KEYS.utmCampaign, tracking.utmCampaign);
  params.set(PARAM_KEYS.campaignId, tracking.campaignId);
  if (tracking.utmContent) params.set(PARAM_KEYS.utmContent, tracking.utmContent);
  if (tracking.utmTerm) params.set(PARAM_KEYS.utmTerm, tracking.utmTerm);

  return `${path}?${params.toString()}${hash}`;
};

/**
 * Build the campaign-tagged link for a file by its id, pointing at the BACKEND
 * REDIRECT endpoint (NOT the raw S3/CDN asset URL). Only hits that go through
 * `${backendOrigin}/api/f/<fileId>` are visible to the file access-analytics
 * feature; links straight to S3 are invisible and un-attributable.
 *
 * TODO(consolidate): when the analytics feature ships its shared helper at
 * src/components/files/utils/shareUrl.js (`buildTrackedFileUrl(fileId, params)`),
 * this function should delegate the base-URL construction to it and only own the
 * campaign param derivation. Until then we hard-code the same `/api/f/<fileId>`
 * shape. Do NOT point campaign links at raw S3 (assets.*) -- those are invisible
 * to analytics.
 *
 * @param {string} fileId   the public file / brochure id
 * @param {object} campaign { id, name, data?: { tracking?: {...} } }
 * @param {object} [opts]   { backendOrigin } e.g. "https://backend.prod.oelement.net"
 * @returns {string} the tracked, campaign-tagged URL
 */
export const buildCampaignTrackedFileUrl = (fileId, campaign, opts = {}) => {
  if (!fileId || typeof fileId !== "string") {
    throw new Error("buildCampaignTrackedFileUrl: fileId is required");
  }
  const backendOrigin = (opts.backendOrigin || DEFAULT_BACKEND_ORIGIN).replace(/\/+$/, "");
  const base = `${backendOrigin}/api/f/${encodeURIComponent(fileId)}`;
  return buildCampaignFileUrl(base, campaign);
};

/**
 * Reference decoder for the attribution seam. Given a tagged URL, recover the
 * campaign attribution the analytics feature will key off. Returns null values
 * for any param that is absent (an untagged / organic hit).
 *
 * @param {string} taggedUrl
 * @returns {{utmSource,utmMedium,utmCampaign,utmContent,utmTerm,campaignId}}
 */
export const parseCampaignFileUrl = (taggedUrl) => {
  const url = String(taggedUrl == null ? "" : taggedUrl);
  const hashIndex = url.indexOf("#");
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const qIndex = withoutHash.indexOf("?");
  const queryStr = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : "";
  const params = new URLSearchParams(queryStr);
  return {
    utmSource: params.get(PARAM_KEYS.utmSource),
    utmMedium: params.get(PARAM_KEYS.utmMedium),
    utmCampaign: params.get(PARAM_KEYS.utmCampaign),
    utmContent: params.get(PARAM_KEYS.utmContent),
    utmTerm: params.get(PARAM_KEYS.utmTerm),
    campaignId: params.get(PARAM_KEYS.campaignId),
  };
};
